/**
 * The cockpit's route exports (G2 test d, ADR 9010/9041) — `docs/adr/9046`.
 *
 * Each page's tables carry their own CSV and Excel of what they draw (the states, the groups, the
 * stores, the sizes). These are the ROUTE's exports the manifest registers, taken from the URL:
 *
 *   levels 0 and 1   the network-health read's groups, with every count it publishes — none while
 *                    the read is not pinned (an empty file, the perimeter saying so)
 *   levels 2 and 3   the run's stock cells the page judged, as `GET /inseason/stock-positions`
 *                    publishes them — the evidence behind every state on the page — each with the
 *                    page's own judgement of it (`./cells-file.ts`, bata-ui#302 C): its state, why
 *                    it is not evaluable, whether a line covers it, and what is beside it. Each cell
 *                    also names its store and article (client request, owner answers 2026-11-03):
 *                    the store's name, and the article's brand, gender, major category, category
 *                    and risk, from `POST /health/attributes` on the stock date
 *                    (`@/planning/health-attributes`) — `missing` where the platform gives none
 *
 * **The groups file says each count by its grain** (bata-ui#302 C): the cells by state and the
 * uncovered cells by state (the partition, cell grain), the not-evaluable cells by reason, and the
 * sweep's size-gap observations under their own name — «Buchi di taglia osservati», not «Taglie
 * rotte», because `cellsSizeGap` counts what the sweep observed, which is not the cells in the
 * broken state.
 */

import { callOperation, collectPages, readVocabularyLabels, VOCABULARY_DEPARTMENT_FAMILY, type VocabularyLabeller } from '@/api';
import {
  effectiveParameter, foundationsListExceptions, inseasonListProposals, inseasonListRuns, listExceptionRules, listMoveCapDeferrals,
  listStockPositions, listTransfers, type AlertSummary, type StockPositionSummary,
} from '@/api/generated';
import {
  HEALTH_STATES, healthPage, NETWORK_HEALTH_READ, NETWORK_HEALTH_SIZES_READ, networkHealthParams, networkHealthSizesParams, PROBLEM_STATES,
  sizeHealthOf, sizesBesideOf, type HealthCounts, type NetworkHealthRead, type NetworkHealthSizesRead, type ProblemState,
} from '@/api/pending/network-health';
import { buildVersion, NO_PRINCIPAL, NOT_PUBLISHED, type ExportCell, type ExportColumn, type ExportRequest, type ProvenanceFilter } from '@/export';
import type { ExportScope } from '@/features/manifest';
import { i18next } from '@/i18n';
import {
  ARTICLE_ATTRIBUTE_COLUMNS, NO_ATTRIBUTES, STORE_NAME_COLUMN, articleAttributeCells, readHealthAttributes, storeNameCell,
  type HealthAttributes,
} from '@/planning/health-attributes';

import { RUN_ID_PARAM } from '../scope';

import { VERDICT_COLUMNS, verdictCells, type CellJudgement } from './cells-file';
import { primePullReasonLabels } from './pull-reasons';
import { stockDate } from './reads';
import { readNetworkHealthPath, readNetworkHealthScope, type LevelOneDimension } from './routes';
import {
  COVER_FLOOR_PARAMETER, COVER_RULE_ID, criterionOf, deferredStoreArticles, platformReason, SIZE_RUN_RULE_ID, storeArticleKey,
  storeArticleVerdict, withPlatformStates, type Criteria, type StoreArticleVerdict,
} from './size-state';

function request(id: string, titleKey: string, columns: readonly ExportColumn[], rows: readonly (readonly ExportCell[])[], filters: readonly ProvenanceFilter[], definition: string): ExportRequest {
  return {
    id,
    titleKey,
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: id, definition, registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters, sort: [], visibleColumns: columns.map((column) => column.key), drillPath: [], densityMode: 'compact' },
  };
}

const num = (value: number | null): ExportCell => (value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value });
const text = (value: string | null): ExportCell => (value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value });

/** Each state's column header — literal, so the catalogue check sees every key. */
const STATE_HEADER: Readonly<Record<(typeof HEALTH_STATES)[number], string>> = {
  ok: 'inseason.networkHealth.export.cellsState.ok',
  low: 'inseason.networkHealth.export.cellsState.low',
  out: 'inseason.networkHealth.export.cellsState.out',
  broken: 'inseason.networkHealth.export.cellsState.broken',
  na: 'inseason.networkHealth.export.cellsState.na',
};
const UNCOVERED_HEADER: Readonly<Record<(typeof PROBLEM_STATES)[number], string>> = {
  low: 'inseason.networkHealth.export.uncoveredState.low',
  out: 'inseason.networkHealth.export.uncoveredState.out',
  broken: 'inseason.networkHealth.export.uncoveredState.broken',
};

/**
 * The platform's `cellsNaByReason` codes (bata-platform `network_health.py`), one column each; a
 * code this build does not list goes to «other reasons» with its code, never dropped.
 */
export const NA_REASON_CODES = [
  'position_not_reported', 'on_hand_not_counted', 'partition_stale', 'demand_below_floor', 'demand_not_forecast', 'no_demand',
  'cover_rule_not_applicable',
] as const;
const NA_HEADER: Readonly<Record<(typeof NA_REASON_CODES)[number], string>> = {
  position_not_reported: 'inseason.networkHealth.export.naReason.position_not_reported',
  on_hand_not_counted: 'inseason.networkHealth.export.naReason.on_hand_not_counted',
  partition_stale: 'inseason.networkHealth.export.naReason.partition_stale',
  demand_below_floor: 'inseason.networkHealth.export.naReason.demand_below_floor',
  demand_not_forecast: 'inseason.networkHealth.export.naReason.demand_not_forecast',
  no_demand: 'inseason.networkHealth.export.naReason.no_demand',
  cover_rule_not_applicable: 'inseason.networkHealth.export.naReason.cover_rule_not_applicable',
};

export const GROUP_COLUMNS: readonly ExportColumn[] = [
  { key: 'group_key', headerKey: 'inseason.networkHealth.export.groupKey', descriptionKey: 'inseason.networkHealth.columnGuide.group.groupKey', kind: 'text' },
  { key: 'group_label', headerKey: 'inseason.networkHealth.export.groupLabel', descriptionKey: 'inseason.networkHealth.columnGuide.group.groupLabel', kind: 'text' },
  { key: 'cells_served', headerKey: 'inseason.networkHealth.l1.column.served', descriptionKey: 'inseason.networkHealth.columnGuide.group.served', kind: 'number' },
  // The partition, cell grain (bata-ui#302 C): every served cell in exactly one state.
  ...HEALTH_STATES.map((state): ExportColumn => ({
    key: `cells_${state}`, headerKey: STATE_HEADER[state], kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.cellsByState',
  })),
  ...PROBLEM_STATES.map((state): ExportColumn => ({
    key: `cells_${state}_uncovered`, headerKey: UNCOVERED_HEADER[state], kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.cellsUncoveredByState',
  })),
  ...NA_REASON_CODES.map((code): ExportColumn => ({
    key: `cells_na_${code}`, headerKey: NA_HEADER[code], kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.cellsNaByReason',
  })),
  {
    key: 'cells_na_other', headerKey: 'inseason.networkHealth.export.naReason.other', kind: 'text', descriptionKey: 'inseason.networkHealth.columnGuide.cellsNaOther',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.export.absent.noOtherReason' } },
  },
  { key: 'cells_oos', headerKey: 'inseason.networkHealth.export.oosCells', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.oosCells' },
  { key: 'cells_oos_uncovered', headerKey: 'inseason.networkHealth.export.oosCellsUncovered', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.oosCellsUncovered' },
  { key: 'store_articles_oos', headerKey: 'inseason.networkHealth.export.oosStoreArticles', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.oosStoreArticles' },
  // Cover and size gaps are the sweep's: where it did not observe the run the cell is absent, never 0 (DEC-110).
  {
    key: 'store_articles_below_cover', headerKey: 'inseason.networkHealth.export.lowStoreArticles', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.lowCover',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.coverNotObserved' } },
  },
  {
    key: 'store_articles_below_cover_uncovered', headerKey: 'inseason.networkHealth.export.lowStoreArticlesUncovered', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.lowCover',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.coverNotObserved' } },
  },
  // `cellsSizeGap` is the sweep's observations, NOT the broken state (bata-ui#302 C): named so.
  {
    key: 'cells_size_gap_observed', headerKey: 'inseason.networkHealth.export.sizeGapObserved', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.sizeGapObserved',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.sizeGapNotObserved' } },
  },
  {
    key: 'cells_size_gap_observed_uncovered', headerKey: 'inseason.networkHealth.export.sizeGapObservedUncovered', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.sizeGapObserved',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.sizeGapNotObserved' } },
  },
  { key: 'proposal_units', headerKey: 'inseason.networkHealth.export.proposalUnits', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.proposalUnits' },
  { key: 'transfer_in_units', headerKey: 'inseason.networkHealth.export.transferInUnits', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.transferInUnits' },
  { key: 'transfer_out_units', headerKey: 'inseason.networkHealth.export.transferOutUnits', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.transferOutUnits' },
  { key: 'store_articles_with_open_alert', headerKey: 'inseason.networkHealth.export.openAlerts', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.group.openAlerts' },
  // Beside the uncovered, never cover (F1/F2, bata-platform#559).
  { key: 'cells_uncovered_with_open_alert', headerKey: 'inseason.networkHealth.export.uncoveredWithOpenAlert', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.uncoveredWithOpenAlert' },
  {
    key: 'cells_uncovered_with_price_step', headerKey: 'inseason.networkHealth.export.uncoveredWithPriceStep', kind: 'number', descriptionKey: 'inseason.networkHealth.columnGuide.uncoveredWithPriceStep',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.noPriceRun' } },
  },
];

/**
 * A group's name exactly as the groups page says it (`GroupsScreen`'s `useGroupName`): the
 * platform's label, else — for a department family — the vocabulary's label of its code, else its
 * key (the code stands only where the page's does); the no-value bucket in words.
 */
export function groupName(group: HealthCounts, by: LevelOneDimension | 'country', familyLabel: VocabularyLabeller, noValue: string): string {
  return group.label
    ?? (group.key !== null && by === 'department_family' ? familyLabel(group.key) : group.key)
    ?? noValue;
}

/**
 * One group's row. `name` is the group's name as the page says it ({@link groupName}); left out, the
 * row carries the platform's own label, `not reported` where it publishes none (the totals row).
 */
export function groupRow(group: HealthCounts, name?: string): readonly ExportCell[] {
  const states = group.partition.kind === 'drawn' ? group.partition.states : null;
  const others = Object.entries(group.naByReason)
    .filter(([code]) => !(NA_REASON_CODES as readonly string[]).includes(code))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, count]) => [code, String(count)].join('='));
  return [
    text(group.key), text(name === undefined ? group.label : name), num(group.served),
    // A partition the read did not publish whole is not-reported, never zeros (`healthCounts`).
    ...HEALTH_STATES.map((state) => num(states === null ? null : states[state].cells)),
    ...PROBLEM_STATES.map((state) => num(states === null ? null : states[state].uncovered)),
    // The read publishes every reason it found: a reason it does not list counted no cell.
    ...NA_REASON_CODES.map((code) => num(group.naByReason[code] ?? 0)),
    others.length === 0 ? { kind: 'missing', state: 'not-ranged' } : { kind: 'text', value: others.join('; ') },
    num(group.oosCells), num(group.oosCellsUncovered), num(group.oosStoreArticles),
    // DEC-110: where the sweep did not observe the run, cover and size gaps are unknown, never 0
    // (`coverNotObserved` / `sizeGapNotObserved`) — the page's `networkFigures` says the same.
    num(group.coverNotObserved ? null : group.lowCoverStoreArticles), num(group.coverNotObserved ? null : group.lowCoverStoreArticlesUncovered),
    num(group.sizeGapNotObserved ? null : group.brokenCells), num(group.sizeGapNotObserved ? null : group.brokenCellsUncovered),
    num(group.interventions.proposalUnits), num(group.interventions.transferInUnits), num(group.interventions.transferOutUnits),
    num(group.interventions.openAlertStoreArticles),
    num(group.uncoveredWithOpenAlert), num(group.uncoveredWithPriceStep),
  ];
}

/**
 * The file's title per grouping, with no placeholder (issue 249): «Salute della rete per {by}» named
 * the workbook `…-per-{by}_…` and its sheet fell back to the export id. Spelled out so the
 * catalogue check sees every key.
 */
export const GROUPS_TITLE_KEY: Readonly<Record<LevelOneDimension | 'country', string>> = {
  country: 'inseason.networkHealth.export.title.country',
  department_family: 'inseason.networkHealth.export.title.department_family',
  store: 'inseason.networkHealth.export.title.store',
  article: 'inseason.networkHealth.export.title.article',
};

/** Groups asked per page by the file — the read's maximum, so 291 stores are one request. */
export const NETWORK_HEALTH_EXPORT_PAGE = 500;
/** A bound on the walk, so a cursor that never ends cannot hang the download. */
const MAX_GROUP_PAGES = 200;

/**
 * EVERY group of the read, in the platform's order, page after page (`nextCursor`) — the file
 * carries the whole set the page counts, not the fifty it draws (ADR 9034, owner ruling
 * 2026-10-01; issue 249). The totals are the first page's: they do not depend on the page.
 */
export async function readAllGroups(
  read: NetworkHealthRead, runId: string, by: LevelOneDimension | 'country', departmentFamily: string | null,
  states: readonly ProblemState[] = [],
): Promise<{ readonly groups: readonly HealthCounts[]; readonly totals: HealthCounts }> {
  const groups: HealthCounts[] = [];
  let cursor: string | null = null;
  let totals: HealthCounts | null = null;
  for (let pages = 0; pages < MAX_GROUP_PAGES; pages += 1) {
    const params = networkHealthParams(runId, by, { departmentFamily, states, limit: NETWORK_HEALTH_EXPORT_PAGE });
    const answer = await callOperation(read, cursor === null ? params : { ...params, query: { ...params.query, cursor } }, {});
    const page = healthPage(answer);
    totals ??= page.totals;
    groups.push(...page.groups);
    if (page.nextCursor === null || page.nextCursor === '' || page.groups.length === 0) return { groups, totals };
    cursor = page.nextCursor;
  }
  throw new Error(`network-health: more than ${MAX_GROUP_PAGES} pages of groups`);
}

async function groupsRequest(id: string, given: ExportScope | undefined, by: LevelOneDimension | 'country', read: NetworkHealthRead | null): Promise<ExportRequest> {
  const scope = readNetworkHealthScope(given?.search ?? '');
  const runId = scope.runId;
  const filters: ProvenanceFilter[] = [
    runId === null ? { field: 'run_id', operator: 'unset', value: '' } : { field: 'run_id', operator: 'equals', value: runId },
    { field: 'group_by', operator: 'equals', value: by },
    ...(scope.department === null ? [] : [{ field: 'department_family', operator: 'equals', value: scope.department }]),
    // The file carries the groups the page lists: narrowed by state (the KPI cards' links), the same ones.
    ...(by === 'country' || scope.states.length === 0 ? [] : [{ field: 'state', operator: 'in', value: scope.states.join(',') }]),
  ];
  const definition = 'DEC-107: every served store x article x size of the run (served = in the stock partition and not not_ranged), its cells by state and uncovered by state (cell grain), its not-evaluable cells by reason, its stock-outs, low cover, the sweep’s size-gap observations and the run’s interventions, as GET /inseason/runs/{run_id}/network-health counts them (bata-platform ADR 6055).';
  const titleKey = GROUPS_TITLE_KEY[by];
  if (read === null || runId === null) return request(id, titleKey, GROUP_COLUMNS, [], filters, definition);
  if (by === 'country') {
    const page = healthPage(await callOperation(read, networkHealthParams(runId, by, { departmentFamily: scope.department }), {}));
    return request(id, titleKey, GROUP_COLUMNS, [groupRow(page.totals)], filters, definition);
  }
  const { groups } = await readAllGroups(read, runId, by, scope.department, scope.states);
  // The page names a family from the vocabulary where the platform sends no label: read the page's own
  // cache entry, and only when some group needs it (a file is never blocked by it, and never walks).
  const needsWords = by === 'department_family' && groups.some((group) => group.label === null && group.key !== null);
  const familyLabel = needsWords ? (await readVocabularyLabels(VOCABULARY_DEPARTMENT_FAMILY, i18next.language)).labelOf : (code: string) => code;
  const noValue = i18next.t('inseason.networkHealth.l1.noValue');
  return request(id, titleKey, GROUP_COLUMNS, groups.map((group) => groupRow(group, groupName(group, by, familyLabel, noValue))), filters, definition);
}

export function networkExport(given?: ExportScope): Promise<ExportRequest> {
  return groupsRequest('inseason.networkHealth.network', given, 'country', NETWORK_HEALTH_READ);
}

export function groupsExport(given?: ExportScope): Promise<ExportRequest> {
  const scope = readNetworkHealthScope(given?.search ?? '');
  return groupsRequest('inseason.networkHealth.groups', given, scope.department === null ? scope.by : 'article', NETWORK_HEALTH_READ);
}

const CELL_COLUMNS: readonly ExportColumn[] = [
  { key: 'store_key', headerKey: 'inseason.networkHealth.l2.column.store', descriptionKey: 'inseason.columnGuide.stockPosition.storeKey', kind: 'text' },
  STORE_NAME_COLUMN,
  { key: 'article_key', headerKey: 'inseason.networkHealth.l2.column.article', descriptionKey: 'inseason.columnGuide.stockPosition.articleKey', kind: 'text' },
  ...ARTICLE_ATTRIBUTE_COLUMNS,
  { key: 'size_key', headerKey: 'inseason.networkHealth.export.sizeKey', descriptionKey: 'inseason.columnGuide.stockPosition.sizeKey', kind: 'text' },
  { key: 'size_code', headerKey: 'inseason.networkHealth.l3.row.size', descriptionKey: 'inseason.columnGuide.stockPosition.sizeCode', kind: 'text' },
  { key: 'position_state', headerKey: 'inseason.networkHealth.export.positionState', descriptionKey: 'inseason.columnGuide.stockPosition.positionState', kind: 'text' },
  { key: 'on_hand_units', headerKey: 'inseason.networkHealth.l3.row.stock', descriptionKey: 'inseason.columnGuide.stockPosition.onHand', kind: 'number' },
  { key: 'committed_units', headerKey: 'inseason.networkHealth.export.committedUnits', descriptionKey: 'inseason.columnGuide.stockPosition.committed', kind: 'number' },
  { key: 'weekly_demand', headerKey: 'inseason.networkHealth.export.weeklyDemand', descriptionKey: 'inseason.columnGuide.stockPosition.weeklyDemand', kind: 'number' },
  { key: 'weeks_of_cover', headerKey: 'inseason.networkHealth.l3.row.cover', descriptionKey: 'inseason.columnGuide.stockPosition.cover', kind: 'number' },
  { key: 'partition_is_fresh', headerKey: 'inseason.networkHealth.export.fresh', descriptionKey: 'inseason.columnGuide.stockPosition.fresh', kind: 'text' },
];

/** The cells file's columns: the position's own, then the page's judgement (`./cells-file.ts`). */
export const ALL_CELL_COLUMNS: readonly ExportColumn[] = [...CELL_COLUMNS, ...VERDICT_COLUMNS];

export function cellRow(row: StockPositionSummary, judgement: CellJudgement, attributes: HealthAttributes = NO_ATTRIBUTES): readonly ExportCell[] {
  return [
    text(row.store_key), storeNameCell(attributes, row.store_key), text(row.article_key), ...articleAttributeCells(attributes, row.article_key),
    text(row.size_key), text(row.size_code ?? null), text(row.position_state),
    num(row.on_hand_units), num(row.committed_units), num(row.weekly_demand), num(row.weeks_of_cover), text(row.partition_is_fresh ? 'true' : 'false'),
    ...verdictCells(row, judgement),
  ];
}

/** A read that may fail: its answer, or `null`. The file then says the judgement was not given. */
async function settle<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch {
    return null;
  }
}

/** A registry answer as `useCriteria` reads it: `undefined` unread, `null` unset, else the value. */
function numberOf(answer: { readonly source: string; readonly numericValue?: number | null } | null): number | null | undefined {
  if (answer === null) return undefined;
  if (answer.source === 'unset') return null;
  return answer.numericValue ?? null;
}

/** The two rules and the demand floor for the run's market on its stock date — `useCriteria`, outside React. */
async function readCriteria(market: string, asOf: string): Promise<Criteria | null> {
  const rules = await settle(callOperation(listExceptionRules, {}));
  if (rules === null) return null;
  const ruleOf = (ruleId: string) => rules.find((rule) => rule.ruleId === ruleId) ?? null;
  const cover = ruleOf(COVER_RULE_ID);
  const sizeGap = ruleOf(SIZE_RUN_RULE_ID);
  const effective = (key: string) => settle(callOperation(effectiveParameter, { query: { parameter_key: key, as_of: asOf, country: market } }));
  const [coverThreshold, gapThreshold, floor] = await Promise.all([
    cover === null ? Promise.resolve(null) : effective(cover.thresholdParameterKey),
    sizeGap === null ? Promise.resolve(null) : effective(sizeGap.thresholdParameterKey),
    effective(COVER_FLOOR_PARAMETER),
  ]);
  return {
    cover: criterionOf(COVER_RULE_ID, cover, cover === null ? null : numberOf(coverThreshold)),
    sizeGap: criterionOf(SIZE_RUN_RULE_ID, sizeGap, sizeGap === null ? null : numberOf(gapThreshold)),
    demandFloor: numberOf(floor),
  };
}

/**
 * The page's judgement of the cells it drew, from the same reads the page makes (`./reads.ts`): the
 * run's market, its proposals and transfers, the open alerts, the rules in force and the move-cap
 * deferrals — and at level 3 the platform's sizes read, overlaid as the grid overlays it. Never
 * rejects: a read that fails leaves its columns `not-reported`.
 */
export async function readCellJudgement(
  runId: string,
  storeKey: string | null,
  articleKey: string | null,
  rows: readonly StockPositionSummary[],
  sizesRead: NetworkHealthSizesRead | null = NETWORK_HEALTH_SIZES_READ,
): Promise<CellJudgement> {
  const narrow = { ...(storeKey === null ? {} : { store_key: storeKey }), ...(articleKey === null ? {} : { article_key: articleKey }) };
  const directory = await settle(collectPages(inseasonListRuns, { query: {} }));
  const market = directory?.find((run) => run.run_id === runId)?.country_key ?? null;
  const asOf = stockDate(rows);
  const [proposals, transfers, alerts, deferrals, criteria, platform] = await Promise.all([
    settle(collectPages(inseasonListProposals, { query: { run_id: runId, ...narrow } })),
    settle(collectPages(listTransfers, { query: { run_id: runId, ...narrow } })),
    market === null ? Promise.resolve(null) : settle(collectPages(foundationsListExceptions, { query: { open_only: true, country_key: market, ...narrow } })),
    settle(collectPages(listMoveCapDeferrals, { path: { run_id: runId }, query: narrow })),
    market === null || asOf === null ? Promise.resolve(null) : readCriteria(market, asOf),
    sizesRead === null || storeKey === null || articleKey === null
      ? Promise.resolve(null)
      : settle(callOperation(sizesRead, networkHealthSizesParams(runId, storeKey, articleKey), {})),
  ]);
  const deferred = deferrals === null ? null : deferredStoreArticles(deferrals);
  if (proposals === null || transfers === null || criteria === null) return { verdicts: null, alertsRead: alerts !== null, deferred };
  const openAlerts: readonly AlertSummary[] = alerts ?? [];
  // Level 3: the platform's states over the local ones, exactly as the grid draws them.
  const states = platform === null ? null : new Map([...sizeHealthOf(platform)].map(([size, health]) => [size, {
    served: health.served, state: health.state, reason: platformReason(health.reasonCode), uncovered: health.uncovered,
    cause: { reason: health.uncoveredReason, targetUnits: health.targetUnits, needUnits: health.needUnits },
  }]));
  // Level 3: what is beside the sizes is the platform's too (F1/F2): its alert rules and price step.
  const beside = platform === null ? null : sizesBesideOf(platform);
  const verdicts = new Map<string, StoreArticleVerdict>();
  for (const row of rows) {
    const key = storeArticleKey(row.store_key, row.article_key);
    if (verdicts.has(key)) continue;
    const local = storeArticleVerdict({
      storeKey: row.store_key, articleKey: row.article_key, articleRows: rows, proposals, transfers, alerts: openAlerts, criteria,
    });
    verdicts.set(key, states === null || beside === null ? local : { ...withPlatformStates(local, states), openAlertRules: beside.openAlertRules });
  }
  const priceStep = beside === null || storeKey === null || articleKey === null
    ? undefined
    : new Map([[storeArticleKey(storeKey, articleKey), beside.hasPriceStep]]);
  return {
    verdicts, alertsRead: alerts !== null || beside !== null, deferred,
    ...(priceStep === undefined ? {} : { priceStep }),
  };
}

/** The labels of the cells' stores and articles, on the run's market and stock date. Never rejects. */
async function readCellAttributes(runId: string, rows: readonly StockPositionSummary[]): Promise<HealthAttributes> {
  const directory = await settle(collectPages(inseasonListRuns, { query: {} }));
  const market = directory?.find((run) => run.run_id === runId)?.country_key ?? null;
  return readHealthAttributes(market, stockDate(rows), rows.map((row) => row.article_key), rows.map((row) => row.store_key));
}

/** Levels 2 and 3: the run's cells behind the page — the article's, the store's, or the store × article's. */
export async function cellsExport(given?: ExportScope): Promise<ExportRequest> {
  const params = new URLSearchParams(given?.search ?? '');
  const runId = params.get(RUN_ID_PARAM);
  const { storeKey, articleKey } = readNetworkHealthPath(given?.path ?? '');
  const filters: ProvenanceFilter[] = [
    runId === null ? { field: 'run_id', operator: 'unset', value: '' } : { field: 'run_id', operator: 'equals', value: runId },
    ...(storeKey === null ? [] : [{ field: 'store_key', operator: 'equals', value: storeKey }]),
    ...(articleKey === null ? [] : [{ field: 'article_key', operator: 'equals', value: articleKey }]),
  ];
  const definition = 'The run’s stock cells (GET /inseason/stock-positions) the page judged: position state, on hand, committed (on hand + in transit), weekly demand and weeks of cover, per store x article x size; then the page’s judgement of each cell (DEC-107/108, owner decisions F1/F2 of 2026-10-05): its health state, why it is not evaluable, whether a non-rejected proposal or inbound transfer covers it (an open alert or a price step never does), the pairs on the way, the open alert rules and whether the weekly move cap deferred a move for the store x article.';
  if (runId === null || (storeKey === null && articleKey === null)) {
    return request('inseason.networkHealth.cells', 'inseason.networkHealth.export.cellsTitle', ALL_CELL_COLUMNS, [], filters, definition);
  }
  const rows = await collectPages(listStockPositions, {
    query: { run_id: runId, ...(storeKey === null ? {} : { store_key: storeKey }), ...(articleKey === null ? {} : { article_key: articleKey }) },
  });
  const [judgement, attributes] = await Promise.all([
    readCellJudgement(runId, storeKey, articleKey, rows),
    readCellAttributes(runId, rows),
  ]);
  // The file's cause column reads the handover causes in the platform's words (`./pull-reasons.ts`).
  await primePullReasonLabels(i18next.language);
  return request(
    'inseason.networkHealth.cells', 'inseason.networkHealth.export.cellsTitle', ALL_CELL_COLUMNS,
    rows.map((row) => cellRow(row, judgement, attributes)), filters, definition,
  );
}
